import React from 'react';
import { HelpCircle } from 'lucide-react';
import { Input } from '../ui/input';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '../ui/tooltip';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../ui/select';

// ── Help Icon ──────────────────────────────────────────────────

function HelpTip({ text }: { text: string }) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <HelpCircle className="h-3 w-3 text-muted-foreground/50 hover:text-muted-foreground cursor-help inline-block ml-1" />
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[200px] text-xs">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ── Number Field ───────────────────────────────────────────────

export function NumberField({
  label,
  value,
  onChange,
  disabled,
  min,
  max,
  step,
  help,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
  help?: string;
}) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-muted-foreground">
        {label}
        {help && <HelpTip text={help} />}
      </label>
      <Input
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        className="h-8 text-sm rounded-lg"
      />
    </div>
  );
}

// ── Color Field ────────────────────────────────────────────────

export function ColorField({
  label,
  value,
  onChange,
  help,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  help?: string;
}) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-muted-foreground">
        {label}
        {help && <HelpTip text={help} />}
      </label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-8 h-8 rounded border cursor-pointer"
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 text-sm rounded-lg flex-1"
        />
      </div>
    </div>
  );
}

// ── Select Field ───────────────────────────────────────────────

export function SelectField({
  label,
  value,
  options,
  onChange,
  help,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  help?: string;
}) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-muted-foreground">
        {label}
        {help && <HelpTip text={help} />}
      </label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-8 w-full text-sm rounded-lg">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// ── Slider Field ───────────────────────────────────────────────

export function SliderField({
  label,
  value,
  onChange,
  min = 0,
  max = 1,
  step = 0.01,
  formatValue,
  help,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  formatValue?: (v: number) => string;
  help?: string;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-muted-foreground">
          {label}
          {help && <HelpTip text={help} />}
        </label>
        <span className="text-xs text-muted-foreground tabular-nums">
          {formatValue ? formatValue(value) : value.toFixed(2)}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-2 bg-muted rounded-full accent-primary cursor-pointer"
      />
    </div>
  );
}

// ── Token Picker ───────────────────────────────────────────────

export function TokenPicker({
  fields,
  sampleData,
  onInsert,
}: {
  fields: string[];
  sampleData?: Record<string, string>;
  onInsert: (value: string) => void;
}) {
  if (fields.length === 0) return null;
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-muted-foreground">Insert Product Data</label>
      <Select
        value=""
        onValueChange={(field) => {
          if (!field) return;
          onInsert(`{{${field}}}`);
        }}
      >
        <SelectTrigger className="h-8 w-full text-sm rounded-lg">
          <SelectValue placeholder="Select field..." />
        </SelectTrigger>
        <SelectContent>
          {fields.map((field) => (
            <SelectItem key={field} value={field}>
              {field}{sampleData?.[field] ? ` — ${sampleData[field].slice(0, 30)}` : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
